import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Paperclip, Send } from 'lucide-react';
import type { Attachment } from '@shared/types';
import { Avatar, Button, CenteredSpinner, ErrorState } from '@/components/ui';
import { cn, formatFileSize, formatRelative } from '@/lib/format';
import {
  useAddComment,
  useLabels,
  useTask,
  useUpdateTask,
  useUsers,
} from '@/lib/queries';
import type { TaskPatch } from '@/lib/api';
import { Lightbox } from '@/components/Lightbox';
import {
  AssigneePicker,
  EditableDate,
  EditablePriority,
  EditableText,
  LabelPicker,
} from '@/components/editors';
import { AttachmentUpload } from '@/components/AttachmentUpload';

export function TaskDetailPage() {
  const { taskId } = useParams({ from: '/shell/tasks/$taskId' });
  const [draft, setDraft] = useState('');
  const [lightbox, setLightbox] = useState<Attachment | null>(null);
  const [editingDesc, setEditingDesc] = useState(false);
  const updateTask = useUpdateTask();
  const addComment = useAddComment(taskId);
  // Live comments; paused while the description editor or a save is open so
  // a poll can't race an optimistic update.
  const live = !editingDesc && !updateTask.isPending && !addComment.isPending;
  const { data, isPending, error, refetch } = useTask(taskId, live);
  const { data: users } = useUsers();
  const { data: labels } = useLabels();

  /** Optimistic patch helper — every editable field routes through here. */
  const save = (patch: TaskPatch) => updateTask.mutate({ id: taskId, patch });

  if (isPending) return <CenteredSpinner label="Aufgabe wird geladen…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const { task, comments } = data;
  const images = (task.attachments ?? []).filter((a) => a.isImage);
  const files = (task.attachments ?? []).filter((a) => !a.isImage);

  function submitComment(e: React.FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    // Plain text typed by the user; wrap in a paragraph so it renders
    // consistently with the imported HTML comments. Escaped server-side.
    addComment.mutate(`<p>${text.replace(/\n/g, '<br>')}</p>`, {
      onSuccess: () => setDraft(''),
    });
  }

  return (
    <div className="scroll-thin h-full overflow-y-auto">
      {/* Fill the available width rather than a narrow centred column — a wide
          screen was leaving big empty margins. A generous cap keeps line
          lengths sane on ultrawide monitors without pinning edge-to-edge. */}
      <div className="mx-auto w-full max-w-[1400px] px-6 py-5 xl:px-10">
        <div className="mb-4 flex items-center gap-2">
          <Link
            to="/projects/$projectId"
            params={{ projectId: task.projectId }}
            className="flex items-center gap-1.5 text-xs text-muted hover:text-strong"
          >
            <ArrowLeft className="size-3.5" />
            Zurück zum Projekt
          </Link>
          <span className="font-mono text-[11px] text-faint">{task.identifier}</span>
        </div>

        <div className="rounded-[var(--radius-card)] surface-card border line p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={task.done}
              onChange={() => updateTask.mutate({ id: task.id, patch: { done: !task.done } })}
              className="mt-1 size-5 shrink-0 cursor-pointer rounded accent-brand-500"
              aria-label={task.done ? 'Als offen markieren' : 'Als erledigt markieren'}
            />
            <div className="flex-1">
              <EditableText
                value={task.title}
                onSave={(title) => save({ title })}
                className={cn('text-xl font-semibold leading-snug text-strong', task.done && 'line-through opacity-60')}
              />
            </div>
          </div>

          {/* Editable metadata grid. Every field commits an optimistic patch. */}
          <div className="mt-4 grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2.5 text-xs">
            <span className="text-faint">Fällig</span>
            <EditableDate value={task.dueDate} onSave={(v) => save({ dueDate: v })} label="Fälligkeitsdatum" />

            <span className="text-faint">Start</span>
            <EditableDate value={task.startDate} onSave={(v) => save({ startDate: v })} label="Startdatum" />

            <span className="text-faint">Priorität</span>
            <EditablePriority value={task.priority} onSave={(v) => save({ priority: v })} />

            <span className="text-faint">Zugewiesen</span>
            <AssigneePicker
              users={users ?? []}
              selectedIds={task.assignees.map((u) => u.id)}
              onChange={(ids) => save({ assigneeIds: ids })}
            />

            <span className="text-faint">Labels</span>
            <LabelPicker
              labels={labels ?? []}
              selectedIds={task.labels.map((l) => l.id)}
              onChange={(ids) => save({ labelIds: ids })}
            />
          </div>

          <div className="mt-5 border-t line pt-4">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Beschreibung</h2>
              {!editingDesc && (
                <button onClick={() => setEditingDesc(true)} className="text-[11px] text-brand-500 hover:underline">
                  Bearbeiten
                </button>
              )}
            </div>
            {editingDesc ? (
              <DescriptionEditor
                initial={task.description ?? ''}
                onCancel={() => setEditingDesc(false)}
                onSave={(html) => {
                  save({ description: html });
                  setEditingDesc(false);
                }}
              />
            ) : task.description ? (
              <div
                className="rich-text text-sm text-body"
                dangerouslySetInnerHTML={{ __html: task.description }}
                onClick={(e) => {
                  const el = e.target as HTMLElement;
                  if (el.tagName !== 'IMG') return;
                  const src = el.getAttribute('src');
                  const match = images.find((a) => src?.includes(String(a.id)));
                  if (match) setLightbox(match);
                }}
              />
            ) : (
              <button onClick={() => setEditingDesc(true)} className="text-sm text-faint hover:text-body">
                Beschreibung hinzufügen…
              </button>
            )}
          </div>

          {task.subtasks && task.subtasks.length > 0 && (
            <section className="mt-5 border-t line pt-4">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                Teilaufgaben ({task.subtasks.filter((s) => s.done).length}/{task.subtasks.length})
              </h2>
              <ul className="space-y-1">
                {task.subtasks.map((sub) => (
                  <li key={sub.id} className="flex items-center gap-2 text-sm">
                    <span
                      className={cn(
                        'size-1.5 shrink-0 rounded-full',
                        sub.done ? 'bg-brand-500' : 'bg-[var(--line-strong)]',
                      )}
                      aria-hidden
                    />
                    <Link
                      to="/tasks/$taskId"
                      params={{ taskId: sub.id }}
                      className={cn(
                        'text-body hover:text-brand-500 hover:underline',
                        sub.done && 'text-muted line-through',
                      )}
                    >
                      {sub.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {images.length > 0 && (
            <section className="mt-5 border-t line pt-4">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                Bilder ({images.length})
              </h2>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {images.map((img) => (
                  <button
                    key={img.id}
                    onClick={() => setLightbox(img)}
                    className="group relative aspect-square overflow-hidden rounded-lg border line"
                  >
                    <img
                      src={img.url}
                      alt={img.fileName}
                      loading="lazy"
                      className="size-full object-cover transition-transform group-hover:scale-105"
                    />
                  </button>
                ))}
              </div>
            </section>
          )}

          <section className="mt-5 border-t line pt-4">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
              Anhänge{files.length > 0 ? ` (${files.length})` : ''}
            </h2>
            {files.length > 0 && (
              <ul className="mb-2 space-y-1">
                {files.map((file) => (
                  <li key={file.id}>
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm
                                 hover:bg-[var(--surface-hover)]"
                    >
                      <Paperclip className="size-3.5 shrink-0 text-faint" aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-body">{file.fileName}</span>
                      <span className="shrink-0 text-[11px] text-faint tabular-nums">
                        {formatFileSize(file.fileSize)}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
            <AttachmentUpload taskId={task.id} />
          </section>
        </div>

        <section className="mt-5">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">
            Kommentare ({comments.length})
          </h2>

          <div className="space-y-3">
            {comments.map((c) => (
              <article
                key={c.id}
                className="rounded-[var(--radius-card)] surface-card border line p-3.5"
              >
                <header className="mb-1.5 flex items-center gap-2">
                  {c.author && <Avatar user={c.author} size="xs" />}
                  <span className="text-[13px] font-medium text-strong">
                    {c.author?.name ?? 'Unbekannt'}
                  </span>
                  <time className="text-[11px] text-faint" dateTime={c.created}>
                    {formatRelative(c.created)}
                  </time>
                </header>
                <div
                  className="rich-text text-sm text-body"
                  dangerouslySetInnerHTML={{ __html: c.comment }}
                />
              </article>
            ))}
            {comments.length === 0 && (
              <p className="text-sm text-faint">Noch keine Kommentare.</p>
            )}
          </div>

          <form onSubmit={submitComment} className="mt-3 flex flex-col gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Ctrl/Cmd+Enter submits; plain Enter inserts a newline.
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  submitComment(e);
                }
              }}
              rows={3}
              placeholder="Kommentar schreiben… (Strg+Enter zum Senden)"
              className="w-full resize-y rounded-lg border line surface-card px-3 py-2 text-sm
                         text-strong placeholder:text-faint focus:border-brand-500
                         focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <Button
              type="submit"
              variant="primary"
              size="sm"
              className="self-end"
              loading={addComment.isPending}
              disabled={!draft.trim()}
            >
              <Send className="size-3.5" />
              Senden
            </Button>
          </form>
        </section>
      </div>

      {lightbox && (
        <Lightbox
          images={images}
          current={lightbox}
          onChange={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

/**
 * Description editor.
 *
 * A plain textarea, not a rich-text WYSIWYG: the descriptions are
 * overwhelmingly plain text anyway (verified: 204/204 in the live data), and a
 * textarea keeps newlines — which `.rich-text` renders via pre-wrap and the
 * server autolinks. Typed text is wrapped in a <p> so the stored value stays
 * valid HTML, and the server sanitises it regardless.
 */
function DescriptionEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (html: string) => void;
  onCancel: () => void;
}) {
  // Show existing HTML as editable text: strip tags to plain, keep line breaks.
  const [text, setText] = useState(() =>
    initial
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p>/gi, '\n\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .trim(),
  );

  function commit() {
    const trimmed = text.trim();
    // Empty clears the description (null); otherwise wrap paragraphs.
    if (!trimmed) return onSave('');
    const html = trimmed
      .split(/\n{2,}/)
      .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
      .join('');
    onSave(html);
  }

  return (
    <div className="space-y-2">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        autoFocus
        className="w-full resize-y rounded-lg border line surface-card px-3 py-2 text-sm text-strong
                   focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        placeholder="Beschreibung…"
      />
      <div className="flex gap-2">
        <Button size="sm" variant="primary" onClick={commit}>
          Speichern
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}
